/**
 * Priority alarm checks for the background (screen-off) path.
 *
 * When the app is backgrounded we can't run the full MapView alarm sweep (it
 * needs the live chart cells and the map instance), so this runs the two checks
 * that matter most and stand on their own without the chart: anchor drag and a
 * closing AIS CPA. Both reuse the same pure logic the foreground uses (dragCheck,
 * computeCpa), so a background alarm agrees exactly with the on-screen one.
 *
 * Pure and unit-tested. The runner gathers the inputs from the stores on each
 * background fix and fires a loud notification for whatever this returns, anchor
 * first, then CPA.
 */
import { dragCheck, swingRadiusNm, outsideZone } from './anchorWatch';
import { computeCpa, type Kinematic } from '@/ais/cpa';
import type { AlarmsConfig } from '@/store/alarms';
import { evaluateRouteAlarms, type RouteAlarmInputs } from './routeAlarms';

const M_PER_NM = 1852;

/** Anchor-watch inputs (mirrors the fields the anchor-watch store holds). */
export interface BgAnchorInput {
  anchor: [number, number];
  scopeM: number;
  depthM: number;
  bowHeightM: number;
  bufferM: number;
  /** Which boundary the drag alarm watches: swing circle or custom zone. */
  mode: 'radius' | 'zone';
  /** The custom boundary polygon [lon,lat], used when mode === 'zone'. */
  zone: [number, number][];
}

export interface BgAlarmInputs {
  /** Own ship now. */
  own: Kinematic;
  /** Anchor watch params, or null when not anchored. */
  anchor: BgAnchorInput | null;
  /** AIS targets in range (empty when AIS isn't feeding). */
  aisTargets: (Kinematic & { label: string })[];
  /** The alarm config (thresholds + which alarms are on). */
  config: AlarmsConfig;
  /** Route / goto state for the waypoint-arrival and off-track checks, or null. */
  route?: RouteAlarmInputs | null;
}

export interface BgAlarmHit {
  key: 'anchor' | 'aisCpa' | 'offTrack' | 'waypointArrival';
  title: string;
  body: string;
}

/** Round a distance in nm to a friendly metres string for a notification body. */
function metres(nm: number): string {
  return `${Math.round(nm * M_PER_NM)} m`;
}

/**
 * Evaluate the priority background alarms and return the hits in priority order
 * (anchor drag first, then the worst AIS CPA). Empty when nothing is firing.
 */
export function evaluateBackgroundAlarms(i: BgAlarmInputs): BgAlarmHit[] {
  const hits: BgAlarmHit[] = [];

  // Anchor drag: same boundary logic as the foreground alarm. Custom zone when
  // one is drawn, otherwise the swing circle plus GPS-wander buffer.
  if (i.anchor) {
    const radiusNm = swingRadiusNm({
      scopeM: i.anchor.scopeM,
      depthM: i.anchor.depthM,
      bowHeightM: i.anchor.bowHeightM,
    });
    if (i.anchor.mode === 'zone' && i.anchor.zone.length >= 3) {
      if (outsideZone(i.anchor.zone, i.own.position)) {
        const dc = dragCheck(i.anchor.anchor, i.own.position, radiusNm, i.anchor.bufferM / M_PER_NM);
        hits.push({
          key: 'anchor',
          title: 'Anchor dragging',
          body: `${metres(dc.distanceNm)} from the anchor, outside the drawn anchor zone.`,
        });
      }
    } else {
      const dc = dragCheck(i.anchor.anchor, i.own.position, radiusNm, i.anchor.bufferM / M_PER_NM);
      if (dc.dragging) {
        hits.push({
          key: 'anchor',
          title: 'Anchor dragging',
          body: `${metres(dc.distanceNm)} from the anchor, outside the ${metres(radiusNm)} swing circle.`,
        });
      }
    }
  }

  // AIS CPA: the closest-in-time target that's closing to a small miss distance.
  if (i.config.aisCpaEnabled && i.aisTargets.length) {
    let worst: { label: string; cpaNm: number; tcpaMin: number } | null = null;
    for (const t of i.aisTargets) {
      const r = computeCpa(i.own, t);
      if (r.tcpaMin > 0 && r.tcpaMin <= i.config.aisTcpaMin && r.cpaNm <= i.config.aisCpaNm) {
        if (!worst || r.tcpaMin < worst.tcpaMin) {
          worst = { label: t.label, cpaNm: r.cpaNm, tcpaMin: r.tcpaMin };
        }
      }
    }
    if (worst) {
      hits.push({
        key: 'aisCpa',
        title: 'Collision risk',
        body: `${worst.label} closing: CPA ${metres(worst.cpaNm)} in ${Math.round(worst.tcpaMin)} min.`,
      });
    }
  }

  // Route / goto: off the followed leg (XTD broken), or arrived at a waypoint.
  // Off-track leads the pair - drifting off the leg is the one that can put the
  // boat into trouble, arrival is the softer notification.
  if (i.route) {
    const ra = evaluateRouteAlarms(i.route);
    if (ra.offTrack) {
      hits.push({
        key: 'offTrack',
        title: 'Off track',
        body: `${metres(Math.abs(ra.offTrack.xteNm))} off the leg (limit ${metres(ra.offTrack.xtdNm)}).`,
      });
    }
    if (ra.waypointArrival) {
      hits.push({
        key: 'waypointArrival',
        title: 'Waypoint arrival',
        body: `Arrived at ${ra.waypointArrival.label}.`,
      });
    }
  }

  return hits;
}
